import { useCallback, useEffect } from "react";

import AnnouncementPageId from "@mapmycustomers/shared/enum/announcements/AnnouncementPageId";
import Announcement from "@mapmycustomers/shared/types/announcements/Announcement";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import AnnouncementCta from "@app/types/AnnouncementCta";
import AnnouncementsTrigger from "@app/types/AnnouncementsTrigger";
import getAnnouncementAnalyticsProperties, {
  getAnnouncementButtonAnalyticsProperties,
  getAnnouncementCtaAnalyticsProperties,
} from "@app/util/announcements/getAnnouncementAnalyticsProperties";
import { AnnouncementsCloseSource } from "@app/util/announcements/getAnnouncementsCloseSource";
import useAnalytics from "@app/util/contexts/useAnalytics";

interface AnnouncementsAnalyticsState {
  currentIndex: number;
  open: boolean;
  pageId: AnnouncementPageId | undefined;
  queue: Announcement[];
  trigger: AnnouncementsTrigger | undefined;
}

// Keyed on the queue the store froze for one open, so a remount with the modal open sends nothing twice.
const sentAnnouncementIdsByQueue = new WeakMap<Announcement[], Set<Announcement["id"]>>();

const useAnnouncementsAnalytics = ({
  currentIndex,
  open,
  pageId,
  queue,
  trigger,
}: AnnouncementsAnalyticsState) => {
  const analytics = useAnalytics();
  const total = queue.length;
  const shownAnnouncement: Announcement | undefined = open ? queue[currentIndex] : undefined;

  const trackAnnouncementShown = useDynamicCallback(() => {
    if (!shownAnnouncement) {
      return;
    }
    let sentAnnouncementIds = sentAnnouncementIdsByQueue.get(queue);
    if (!sentAnnouncementIds) {
      sentAnnouncementIds = new Set();
      sentAnnouncementIdsByQueue.set(queue, sentAnnouncementIds);
      analytics.using([], { announcementCount: total, pageId, trigger });
    }
    if (sentAnnouncementIds.has(shownAnnouncement.id)) {
      return;
    }
    sentAnnouncementIds.add(shownAnnouncement.id);
    analytics.using(
      ["Card"],
      getAnnouncementAnalyticsProperties(shownAnnouncement, currentIndex, total, pageId)
    );
  });

  const shownAnnouncementId = shownAnnouncement?.id;
  useEffect(() => {
    if (shownAnnouncementId !== undefined) {
      trackAnnouncementShown();
    }
  }, [shownAnnouncementId, trackAnnouncementShown]);

  const trackNext = useCallback(
    (announcement: Announcement) =>
      analytics.clicked(
        ["Card", currentIndex === total - 1 ? "Done" : "Next"],
        getAnnouncementButtonAnalyticsProperties(announcement, currentIndex, total)
      ),
    [analytics, currentIndex, total]
  );

  const trackRemindLater = useCallback(
    (announcement: Announcement) =>
      analytics.clicked(
        ["Card", "Remind Me Later"],
        getAnnouncementButtonAnalyticsProperties(announcement, currentIndex, total)
      ),
    [analytics, currentIndex, total]
  );

  const trackCtaClick = useCallback(
    (announcement: Announcement, cta: AnnouncementCta) =>
      analytics.clicked(
        ["Card", "CTA"],
        getAnnouncementCtaAnalyticsProperties(
          getAnnouncementAnalyticsProperties(announcement, currentIndex, total, pageId),
          cta
        )
      ),
    [analytics, currentIndex, pageId, total]
  );

  // Every card before the current one was already acted on.
  const trackClose = useCallback(
    (source: AnnouncementsCloseSource) =>
      analytics.clicked(["Close"], {
        ...(shownAnnouncement
          ? getAnnouncementButtonAnalyticsProperties(shownAnnouncement, currentIndex, total)
          : {}),
        postponedCount: total - currentIndex,
        source,
      }),
    [analytics, currentIndex, shownAnnouncement, total]
  );

  return { trackClose, trackCtaClick, trackNext, trackRemindLater };
};

export default useAnnouncementsAnalytics;
